/**
 * PaywallModal Component
 * Premium subscription paywall with compelling value proposition
 *
 * Triggers:
 * - Click on arbitrage CTA
 * - Click on "Voir détails investissement"
 * - Freemium limit reached
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  ScrollView,
  Platform,
  Alert,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useStripe } from '@stripe/stripe-react-native';
import { colors } from '../../theme/colors';
import { typography } from '../../theme/typography';
import { spacing } from '../../theme/spacing';
import { subscriptionsApi } from '../../api/endpoints/subscriptions';
import { salesApi } from '../../api/endpoints/sales';
import logger from '../../utils/logger';

// Conditionally import web payment component
let StripeWebPayment: any = null;
if (Platform.OS === 'web') {
  StripeWebPayment = require('./StripeWebPayment').default;
}

interface PaywallModalProps {
  visible: boolean;
  onClose: () => void;
  onSubscribeSuccess?: () => void;
  trigger?: 'arbitrage' | 'investment' | 'limit' | 'feature';
  potentialGains?: number; // Optional: show potential gains from arbitrage/investment
}

// Benefits list
const PREMIUM_BENEFITS = [
  {
    icon: 'infinity',
    title: 'Messages illimités',
    description: 'Parle avec Wealfi sans limite',
  },
  {
    icon: 'lightning-bolt',
    title: 'Détection arbitrage',
    description: 'Alertes sur les opportunités d\'optimisation',
  },
  {
    icon: 'chart-timeline-variant',
    title: 'Simulateur investissement',
    description: 'Projections PEA, SCPI, Assurance-vie',
  },
  {
    icon: 'bell-ring',
    title: 'Alertes personnalisées',
    description: 'Suivi de tes objectifs en temps réel',
  },
  {
    icon: 'calendar-check',
    title: 'Check-in hebdomadaire',
    description: 'Bilan et conseils chaque semaine',
  },
];

// Testimonial
const TESTIMONIAL = {
  text: '"Wealfi m\'a fait économiser 168€/an en 10 minutes. L\'abonnement s\'est remboursé en moins d\'un mois."',
  author: 'Thomas, 32 ans',
  location: 'Paris',
};

export default function PaywallModal({
  visible,
  onClose,
  onSubscribeSuccess,
  trigger = 'feature',
  potentialGains,
}: PaywallModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [salesActionId, setSalesActionId] = useState<string | null>(null);
  const { initPaymentSheet, presentPaymentSheet } = useStripe();

  // Ref to prevent duplicate tracking on re-renders
  const hasTrackedRef = useRef(false);

  // Track paywall_hit event when modal becomes visible
  useEffect(() => {
    if (visible && !hasTrackedRef.current) {
      hasTrackedRef.current = true;

      salesApi.trackEvent('paywall_hit', {
        feature: trigger,
        potential_gains: potentialGains
      })
        .then((response) => {
          logger.log('[PaywallModal] Tracked paywall_hit:', response);
          if (response.sales_action_id) {
            setSalesActionId(response.sales_action_id);
          }
        })
        .catch((error) => {
          logger.error('[PaywallModal] Failed to track paywall_hit:', error);
        });
    }

    // Reset tracking ref when modal closes
    if (!visible) {
      hasTrackedRef.current = false;
      setSalesActionId(null);
    }
  }, [visible, trigger, potentialGains]);

  // Handle payment success
  const handlePaymentSuccess = () => {
    logger.log('[PaywallModal] Payment successful!');
    setShowPaymentForm(false);
    Alert.alert(
      'Bienvenue chez les Premium !',
      'Ton abonnement est maintenant actif. Tu as accès à toutes les fonctionnalités de Wealfi.',
      [{ text: 'Super !' }]
    );
    onClose();
    onSubscribeSuccess?.();
  };

  // Handle payment cancel (web)
  const handlePaymentCancel = () => {
    setShowPaymentForm(false);
  };

  // Handle subscribe - use Payment Sheet on native, Elements form on web
  const handleSubscribe = async () => {
    // Track action click for attribution if we have a sales action id
    if (salesActionId) {
      salesApi.actionClicked(salesActionId)
        .then(() => logger.log('[PaywallModal] Tracked action click'))
        .catch((error) => logger.error('[PaywallModal] Failed to track action click:', error));
    }

    setIsLoading(true);
    try {
      // On web, show the Stripe Elements payment form
      if (Platform.OS === 'web') {
        logger.log('[PaywallModal] Showing web payment form...');
        setShowPaymentForm(true);
        setIsLoading(false);
        return;
      }

      // On native, use in-app Payment Sheet
      // Step 1: Create payment intent on backend
      logger.log('[PaywallModal] Creating payment intent...');
      const paymentData = await subscriptionsApi.createPaymentIntent('premium_monthly');

      if (!paymentData.success || !paymentData.client_secret) {
        throw new Error('Failed to create payment intent');
      }

      logger.log('[PaywallModal] Payment intent created, initializing sheet...');

      // Step 2: Initialize Payment Sheet
      const { error: initError } = await initPaymentSheet({
        merchantDisplayName: 'Wealfi',
        customerId: paymentData.customer_id,
        customerEphemeralKeySecret: paymentData.ephemeral_key,
        paymentIntentClientSecret: paymentData.client_secret,
        defaultBillingDetails: {
          address: {
            country: 'FR',
          },
        },
        allowsDelayedPaymentMethods: false,
      });

      if (initError) {
        logger.error('[PaywallModal] Init error:', initError);
        throw new Error(initError.message);
      }

      logger.log('[PaywallModal] Payment sheet initialized, presenting...');

      // Step 3: Present Payment Sheet
      const { error: paymentError } = await presentPaymentSheet();

      if (paymentError) {
        if (paymentError.code === 'Canceled') {
          // User cancelled - don't show error
          logger.log('[PaywallModal] User cancelled payment');
          return;
        }
        logger.error('[PaywallModal] Payment error:', paymentError);
        throw new Error(paymentError.message);
      }

      // Step 4: Payment successful! Confirm subscription with backend
      logger.log('[PaywallModal] Payment succeeded! Confirming subscription...');
      try {
        const confirmResult = await subscriptionsApi.confirmSubscription(paymentData.subscription_id);
        logger.log('[PaywallModal] Subscription confirmed:', confirmResult);
      } catch (confirmError) {
        logger.error('[PaywallModal] Failed to confirm subscription:', confirmError);
        // Continue anyway since payment went through
      }

      handlePaymentSuccess();

    } catch (error: any) {
      logger.error('[PaywallModal] Checkout error:', error);
      Alert.alert(
        'Erreur',
        error.message || 'Une erreur est survenue. Veuillez réessayer.',
        [{ text: 'OK' }]
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Dynamic header based on trigger
  const getHeader = () => {
    switch (trigger) {
      case 'arbitrage':
        return {
          emoji: '💰',
          title: 'Débloquer l\'opportunité',
          subtitle: potentialGains
            ? `Potentiel: +${potentialGains.toLocaleString('fr-FR')}€/an`
            : 'Accède au plan d\'action détaillé',
        };
      case 'investment':
        return {
          emoji: '📈',
          title: 'Simulateur Premium',
          subtitle: 'Projections personnalisées sur 10+ ans',
        };
      case 'limit':
        return {
          emoji: '🔒',
          title: 'Limite atteinte',
          subtitle: 'Passe en Premium pour continuer',
        };
      default:
        return {
          emoji: '🚀',
          title: 'Deviens Membre Premium',
          subtitle: 'Accès complet à Wealfi',
        };
    }
  };

  const header = getHeader();

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
    >
      <View style={styles.container}>
        {/* Close button */}
        <TouchableOpacity style={styles.closeButton} onPress={showPaymentForm ? handlePaymentCancel : onClose}>
          <Icon name="close" size={24} color={colors.textSecondary} />
        </TouchableOpacity>

        {/* Web Payment Form */}
        {Platform.OS === 'web' && showPaymentForm && StripeWebPayment && (
          <View style={styles.paymentFormContainer}>
            <StripeWebPayment
              onSuccess={handlePaymentSuccess}
              onCancel={handlePaymentCancel}
              priceKey="premium_monthly"
            />
          </View>
        )}

        {/* Regular Paywall Content - hidden when payment form is showing */}
        {!showPaymentForm && (
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.emoji}>{header.emoji}</Text>
            <Text style={styles.title}>{header.title}</Text>
            <Text style={styles.subtitle}>{header.subtitle}</Text>
          </View>

          {/* Pricing */}
          <View style={styles.pricingCard}>
            <View style={styles.pricingHeader}>
              <Text style={styles.badge}>OFFRE EARLY ACCESS</Text>
            </View>
            <View style={styles.priceRow}>
              <Text style={styles.oldPrice}>14,99€</Text>
              <Text style={styles.currentPrice}>9,99€</Text>
              <Text style={styles.period}>/mois</Text>
            </View>
            <Text style={styles.savings}>Économise 60€/an</Text>
          </View>

          {/* Benefits */}
          <View style={styles.benefitsSection}>
            <Text style={styles.benefitsTitle}>Tout ce qui est inclus :</Text>
            {PREMIUM_BENEFITS.map((benefit, index) => (
              <View key={index} style={styles.benefitItem}>
                <View style={styles.benefitIcon}>
                  <Icon name={benefit.icon} size={20} color={colors.primary} />
                </View>
                <View style={styles.benefitText}>
                  <Text style={styles.benefitTitle}>{benefit.title}</Text>
                  <Text style={styles.benefitDescription}>
                    {benefit.description}
                  </Text>
                </View>
              </View>
            ))}
          </View>

          {/* Testimonial */}
          <View style={styles.testimonialCard}>
            <Icon name="format-quote-open" size={24} color={colors.primary} />
            <Text style={styles.testimonialText}>{TESTIMONIAL.text}</Text>
            <Text style={styles.testimonialAuthor}>
              — {TESTIMONIAL.author}, {TESTIMONIAL.location}
            </Text>
          </View>

          {/* ROI calculation if potential gains provided */}
          {potentialGains && potentialGains > 0 && (
            <View style={styles.roiCard}>
              <Icon name="calculator" size={20} color="#10B981" />
              <Text style={styles.roiText}>
                Avec +{potentialGains.toLocaleString('fr-FR')}€/an de gains,
                l'abonnement se rembourse en{' '}
                <Text style={styles.roiBold}>
                  {Math.ceil((9.99 * 12) / potentialGains)} mois
                </Text>
              </Text>
            </View>
          )}

          {/* Guarantee */}
          <View style={styles.guaranteeRow}>
            <Icon name="shield-check" size={16} color={colors.textSecondary} />
            <Text style={styles.guaranteeText}>
              Annulation gratuite à tout moment
            </Text>
          </View>
        </ScrollView>
        )}

        {/* CTA Buttons - hidden when payment form is showing */}
        {!showPaymentForm && (
        <View style={styles.ctaContainer}>
          <TouchableOpacity
            style={[styles.subscribeButton, isLoading && styles.buttonDisabled]}
            onPress={handleSubscribe}
            disabled={isLoading}
            activeOpacity={0.8}
          >
            {isLoading ? (
              <Text style={styles.subscribeButtonText}>Chargement...</Text>
            ) : (
              <>
                <Icon name="lock-open" size={20} color="#FFFFFF" />
                <Text style={styles.subscribeButtonText}>
                  Débloquer maintenant
                </Text>
              </>
            )}
          </TouchableOpacity>

          <TouchableOpacity style={styles.laterButton} onPress={onClose}>
            <Text style={styles.laterButtonText}>Plus tard</Text>
          </TouchableOpacity>
        </View>
        )}
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
    // Ensure full height on web
    ...(Platform.OS === 'web' ? {
      height: '100%',
      minHeight: '100vh',
    } as any : {}),
  },
  paymentFormContainer: {
    flex: 1,
    paddingTop: Platform.OS === 'ios' ? 80 : 60,
    // Ensure payment form has room to scroll on web
    ...(Platform.OS === 'web' ? {
      height: '100%',
      minHeight: 'calc(100vh - 60px)',
      overflow: 'auto',
    } as any : {}),
  },
  closeButton: {
    position: 'absolute',
    top: Platform.OS === 'ios' ? 60 : 20,
    right: 20,
    zIndex: 100,
    padding: spacing.sm,
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 20,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: spacing.lg,
    paddingTop: Platform.OS === 'ios' ? 80 : 60,
    paddingBottom: spacing.xl,
  },

  // Header
  header: {
    alignItems: 'center',
    marginBottom: spacing.xl,
  },
  emoji: {
    fontSize: 48,
    marginBottom: spacing.sm,
  },
  title: {
    fontSize: typography.fontSize.xxl,
    fontWeight: typography.fontWeight.bold,
    color: colors.text,
    textAlign: 'center',
    marginBottom: spacing.xs,
  },
  subtitle: {
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
    textAlign: 'center',
  },

  // Pricing Card
  pricingCard: {
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 16,
    padding: spacing.lg,
    alignItems: 'center',
    marginBottom: spacing.xl,
    borderWidth: 2,
    borderColor: colors.primary,
  },
  pricingHeader: {
    marginBottom: spacing.md,
  },
  badge: {
    backgroundColor: colors.primary,
    color: '#FFFFFF',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: 20,
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.bold,
    letterSpacing: 1,
    overflow: 'hidden',
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  oldPrice: {
    fontSize: typography.fontSize.lg,
    color: colors.textSecondary,
    textDecorationLine: 'line-through',
    marginRight: spacing.sm,
  },
  currentPrice: {
    fontSize: 48,
    fontWeight: typography.fontWeight.bold,
    color: colors.text,
  },
  period: {
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
    marginLeft: spacing.xs,
  },
  savings: {
    fontSize: typography.fontSize.sm,
    color: colors.success,
    fontWeight: typography.fontWeight.medium,
    marginTop: spacing.sm,
  },

  // Benefits
  benefitsSection: {
    marginBottom: spacing.xl,
  },
  benefitsTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
    marginBottom: spacing.md,
  },
  benefitItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: spacing.md,
  },
  benefitIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: `${colors.primary}15`,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.md,
  },
  benefitText: {
    flex: 1,
  },
  benefitTitle: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
    marginBottom: 2,
  },
  benefitDescription: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
  },

  // Testimonial
  testimonialCard: {
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 12,
    padding: spacing.lg,
    marginBottom: spacing.lg,
  },
  testimonialText: {
    fontSize: typography.fontSize.md,
    color: colors.text,
    fontStyle: 'italic',
    lineHeight: 24,
    marginVertical: spacing.sm,
  },
  testimonialAuthor: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    fontWeight: typography.fontWeight.medium,
  },

  // ROI Card
  roiCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#10B98115',
    borderRadius: 12,
    padding: spacing.md,
    marginBottom: spacing.lg,
  },
  roiText: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    color: '#10B981',
    marginLeft: spacing.sm,
    lineHeight: 20,
  },
  roiBold: {
    fontWeight: typography.fontWeight.bold,
  },

  // Guarantee
  guaranteeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.lg,
  },
  guaranteeText: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginLeft: spacing.xs,
  },

  // CTA
  ctaContainer: {
    paddingHorizontal: spacing.lg,
    paddingBottom: Platform.OS === 'ios' ? 40 : 20,
    paddingTop: spacing.md,
    backgroundColor: colors.background,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  subscribeButton: {
    backgroundColor: colors.primary,
    borderRadius: 12,
    paddingVertical: spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.sm,
  },
  buttonDisabled: {
    opacity: 0.7,
  },
  subscribeButtonText: {
    color: '#FFFFFF',
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.bold,
    marginLeft: spacing.sm,
  },
  laterButton: {
    paddingVertical: spacing.md,
    alignItems: 'center',
  },
  laterButtonText: {
    color: colors.textSecondary,
    fontSize: typography.fontSize.md,
  },
});
